import { OptOut } from "commonAssets/components/Forms/OptOut";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import { createElement } from "commonAssets/lib/createElement.js";
import { loadingGif } from "commonAssets/lib/loadingGif.js";
import { validateField } from "commonAssets/lib/validateField.js";
import { generateMd5Hash } from "commonAssets/lib/generateMd5Hash.js";
import { toggleFieldWarning } from "commonAssets/lib/toggleFieldWarning.js";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError.js";
import { clearMessage } from "commonAssets/lib/clearMessage.js";
import { modal } from "commonAssets/lib/modal.js";
import { ConfirmEmailAddress } from "commonAssets/lib/ConfirmEmailAddress.js";
import { safeTag } from "commonAssets/lib/safeTag.js";
import { appDomain, outputDiv, loading, appConfigKey, i18n } from "commonAssets/js/constants";
import formDefault from "../../docs/formDefault.json";

export class customOptOut extends OptOut {
    formatExclusionDeadline(deadlineUtc, timeZone) {
        const date = new Date(deadlineUtc);
        if (Number.isNaN(date.getTime())) return null;

        const formatter = new Intl.DateTimeFormat("en-US", {
            timeZone,
            month: "long",
            day: "numeric",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
            timeZoneName: "short",
        });

        const parts = formatter.formatToParts(date);
        const map = Object.fromEntries(
            parts
                .filter((p) => p.type !== "literal")
                .map((p) => [p.type, p.value])
        );

        const dayPeriod = (map.dayPeriod || "").toUpperCase();
        const dayPeriodWithDots =
            dayPeriod === "AM"
                ? "A.M."
                : dayPeriod === "PM"
                    ? "P.M."
                    : dayPeriod;

        const tz = (map.timeZoneName || "").replace(/^GMT[+-].+$/, "").trim();
        const tzFallback = (() => {
            const tzMap = {
                "America/Los_Angeles": "PT",
                "America/Denver": "MT",
                "America/Chicago": "CT",
                "America/New_York": "ET",
            };
            return tzMap[timeZone] || "";
        })();

        const tzLabel = tz || tzFallback;
        const time = `${map.hour}:${map.minute}:${map.second} ${dayPeriodWithDots}`.trim();
        const tzSuffix = tzLabel ? ` ${tzLabel}` : "";

        return `${map.month} ${map.day}, ${map.year} at ${time}${tzSuffix}`;
    }

    exclusion_form(params) {
        const claimFormElems = [
            createElement("h3", {
                class: "text-center text-decoration-underline mb-3",
            }, [], params.form_options.exclusion_form.title),
            createElement("div", {
                class: "alert alert-info text-dark text-center fw-semibold mb-3",
                id: "exclusion_form_top_notice",
            }, [], i18n.getTranslation('exclusion_form_top_notice')),
            createElement("div", {
                class: "text-center mb-3",
            }, [], params.form_options.exclusion_form.body),
            this.claim_contact_information(params),
            this.claim_documentation(params)
        ];

        if (this.enableClaimFields) {
            claimFormElems.push(this.claim_fieldset(params));
        }

        if (this.enableClaimTransactions) {
            claimFormElems.push(this.claim_transactions(params));
        }

        claimFormElems.push(this.claim_certification(params.form_options.exclusion_form));

        const form = createElement("form", {
            class: "needs-validation",
            id: "form",
            method: "post"
        }, claimFormElems);

        return form;
    }

    async form() {
        try {
            const response = await fetch(this.data);
            const json = await response.json();
            const form = json.claim_form[this.lang];

            if (form?.form_options?.claim_form?.contact_information) {
                form.form_options.claim_form.contact_information.title =
                    "CLASS MEMBER NAME AND CONTACT INFORMATION";
            }

            // Opt-Out guard uses the shared generic claim copy; override it for opt-out only.
            if (form?.form_options?.generic_claim) {
                form.form_options.generic_claim.title = "Generic Opt-Out";
                form.form_options.generic_claim.body =
                    "If you did not receive a personalized Notice in the mail or via email, click below to complete an Opt-Out Form.";
            }

            // Supporting documentation language
            if (form?.claim_upload.documentation.body) {
                form.claim_upload.documentation.body =
                    "You may upload any documents that support your Opt-Out request, such as proof of purchase or transaction records.";
            }

            this.init_exclusion(form);
        } catch (error) {
            this.errorHandler.log_exception(
                new Error("Error fetching claim form", { cause: error })
            );
            this.errorHandler.display_modal(error);
        }
    }

    init_exclusion(form) {
        this.exclusion.api
            .exclusionDeadlineGet({ domain: this.exclusion.data.domain })
            .then((result) => {
                let exclusionDeadlineUtc = null;
                if (result?.data?.exclusion_deadline) {
                    exclusionDeadlineUtc = result.data.exclusion_deadline;
                }

                const timeZone = appDomain?.options?.claimDeadlineTimezone;
                const formatted =
                    exclusionDeadlineUtc && timeZone
                        ? this.formatExclusionDeadline(
                            exclusionDeadlineUtc,
                            timeZone
                        )
                        : null;

                if (form?.form_options?.exclusion_form) {
                    if (formatted) {
                        form.form_options.exclusion_form.body = `The deadline for submitting this Opt-Out Form is <strong>${formatted}</strong>`;
                    } else if (result?.data?.pacific_datetime) {
                        form.form_options.exclusion_form.body = `The deadline for submitting this Opt-Out Form is <strong>${result.data.pacific_datetime} PT</strong>`;
                    }
                }

                // Error Handling (preserve existing behavior)
                if (result?.data?.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                const datetime = result?.data?.pacific_datetime
                    ? result.data.pacific_datetime
                    : `<pre>Error: datetime</pre>`;

                const exclusion_deadline = result?.data?.exclusion_deadline
                    ? result.data.exclusion_deadline
                    : undefined;

                if (form.form_options.exclusion_form.guard.display) {
                    this.init_exclusion_guard(
                        form,
                        container,
                        datetime,
                        exclusion_deadline
                    );
                } else {
                    container.appendChild(this.exclusion_form(form));
                    outputDiv.appendChild(container);

                    this.init_exclusion_fields();

                    document
                        .getElementById("form")
                        .addEventListener("submit", (process) => {
                            this.process_exclusion(
                                process,
                                this.exclusion,
                                exclusion_deadline
                            );
                        });
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching exclusion deadline", {
                        cause: error,
                    })
                );
                this.errorHandler.display(error);
            });
    }

    init_exclusion_fields(params) {
        this.exclusion.flags = {};

        this.exclusion.inputs = Array.from(document.querySelectorAll('#full_name, #first_name, #middle_initial, #last_name, #title, #business_name, #street_address_1, #street_address_2, #country, #city, #state, #zip_code, #province, #postal_code, #email_address, #confirm_email_address, #phone_number_1, #phone_number_2, #signature, #date, #ssn, #referer_url'));
        this.exclusion.inputs.transactions = Array.from(document.querySelectorAll("#transactions input, #transactions textarea, #transactions select"));

        this.exclusion.inputs.forEach(function (field) {
            if (field.hasAttribute('data-validate')) {
                field.addEventListener('change', function (e) {
                    validateField(field);
                });
            }
        });

        if (this.exclusion.inputs.transactions && this.exclusion.inputs.transactions.length > 0) {
            this.exclusion.inputs.transactions.forEach(function (field) {
                if (field.hasAttribute('data-validate')) {
                    field.addEventListener('change', function (e) {
                        validateField(field);
                    });
                }
            });
        }

        this.setup_phone_number_validation();
        this.setup_country_field_toggle();
        this.init_exclusion_files(this.exclusion);

        if (document.getElementById('email_address') && document.getElementById('confirm_email_address')) {
            ConfirmEmailAddress('init');
        }
    }

    init_exclusion_files(exclusion) {
        document.getElementById('file-upload').addEventListener('change', function (e) {
            if (validateField(this)) {
                generateMd5Hash(this.files[0], exclusion);
                document.getElementById('file-description').focus();
            }
        });

        document.getElementById('file-add').addEventListener('click', (e) => {
            this.add_exclusion_file();
        });

        document.getElementById('file-description').addEventListener('keypress', (e) => {
            if (['Enter', 'NumpadEnter'].includes(e.code)) {
                this.add_exclusion_file();
                e.preventDefault();
            }
        });
    }

    add_exclusion_file(e) {
        console.log(`[Add Claim File]`)
        let claim = this.exclusion;

        // add upload support if not already present
        claim.uploads = claim.uploads || [];

        var file = document.getElementById('file-upload');
        var description = document.getElementById('file-description');
        var list = document.getElementById('file-list');
        var fileHash = document.getElementById('file-hash');
        var li;

        if (file.classList.contains('error')) {
            return;
        }

        toggleFieldWarning(file, false);

        toggleFieldError(file, false);
        if (!file.files[0]) {
            toggleFieldError(file, true, 'No File Selected');
            return;
        }

        description.value = description.value.trim();

        toggleFieldError(description, false);
        if (!description.value) {
            description.focus();
            toggleFieldError(description, true, 'Required');
            return;
        }

        li = document.createElement('li');
        li.innerHTML = '<span class="remove" style="cursor: pointer;"><i class="bi bi-x-circle text-danger d-inline-block float-end"></i><span></span></span><span class="filename"><strong>' + safeTag(file.files[0].name) + '</strong></span><span class="description">(' + safeTag(description.value) + ')</span>';
        list.appendChild(li);

        list.classList.remove('d-none');
        document.getElementById('no-files').classList.add('d-none');

        claim.uploads.push({
            file: file.files[0],
            description: description.value,
            hash: fileHash.value
        });

        file.value = '';
        description.value = '';
        fileHash.value = '';

        li.querySelector('.remove').addEventListener('click', removeClaimFile);

        /**
         * Handles the removal of a claim file from the file list and updates 
         * the UI accordingly.
         * 
         * @function removeClaimFile
         * @param {Event} e - The event object triggered by the click action.
         * @description
         * This function performs the following actions:
         * 1. Identifies the clicked row and its parent container.
         * 2. Confirms with the user if they want to remove the file, 
         *      displaying the file name and description.
         * 3. Removes the file from the DOM and the `claim.uploads` array if 
         *      confirmed.
         * 4. Updates the visibility of the file list and the "no files" 
         *      message based on the remaining files.
         * 
         * @since 2025.03.26
         * @version 2025.03.26
         * @returns {void}
         */
        function removeClaimFile(e) {
            let row = this.parentElement;
            let parent = row.parentElement;
            let added = parent.querySelectorAll('li:not([disabled])');
            let index = [].indexOf.call(added, row);
            let confirmText = `Are you sure you want to remove this file? \nFile Name: ${claim.uploads[index].file.name}\nDescription: ${claim.uploads[index].description}`;

            if (window.confirm(confirmText)) {
                parent.removeChild(row);
                claim.uploads.splice(index, 1);
            }

            added = parent.querySelectorAll('li:not([disabled])'); // Recalculate after removal
            document.getElementById('file-list').classList[(added.length == 1) ? 'add' : 'remove']('d-none');
            document.getElementById('no-files').classList[(added.length == 1) ? 'remove' : 'add']('d-none');
        }
    }


    async process_exclusion(e, exclusion, deadline) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();

        if (exclusion.sent) {
            return;
        }

        clearMessage();
        exclusion.valid = true;

        this.exclusion.inputs = Array.from(document.querySelectorAll('#full_name, #first_name, #middle_initial, #last_name, #title, #business_name, #street_address_1, #street_address_2, #country, #city, #state, #zip_code, #province, #postal_code, #email_address, #confirm_email_address, #phone_number_1, #phone_number_2, #signature, #date, #ssn, #referer_url'));
        this.exclusion.inputs.transactions = Array.from(document.querySelectorAll("#transactions input, #transactions textarea, #transactions select"));
        exclusion.data.contact = {};
        exclusion.data.fields = {};

        exclusion.inputs.forEach(function (field) {
            if (field.hasAttribute('data-validate') && !validateField(field)) {
                exclusion.valid = false;
            } else if (field.type == 'radio') {
                if (field.checked) {
                    exclusion.data[field.name] = field.value;
                    exclusion.data.contact[field.name] = field.value;
                }
            } else if (field.type == 'checkbox') {
                if (field.checked) {
                    exclusion.data[field.name] = field.value;
                    exclusion.data.contact[field.name] = field.value;
                } else {
                    exclusion.data[field.name] = 'No';
                    exclusion.data.contact[field.name] = 'No';
                }
            } else {
                exclusion.data[field.name] = field.value;
                exclusion.data.contact[field.name] = field.value;
            }
        });

        if (exclusion.inputs.transactions && exclusion.inputs.transactions.length > 0) {
            exclusion.inputs.transactions.forEach(function (field) {
                const label = field.getAttribute('data-label') || field.name;
                const item_id = field.getAttribute('item-id') || null;

                if (field.hasAttribute('data-validate') && !validateField(field)) {
                    exclusion.flags.valid = false;
                } else if (field.type == 'radio') {
                    if (field.checked) {
                        if (item_id) {
                            if (!exclusion.data.transactions[item_id]) {
                                exclusion.data.transactions[item_id] = {};
                            }
                            exclusion.data.transactions[item_id][label] = field.value;
                        } else {
                            exclusion.data.transactions[label] = field.value;
                        }
                    }
                } else if (field.type == 'checkbox') {
                    if (field.checked) {
                        if (item_id) {
                            if (!exclusion.data.transactions[item_id]) {
                                exclusion.data.transactions[item_id] = {};
                            }
                            exclusion.data.transactions[item_id][label] = field.value;
                        } else {
                            exclusion.data.transactions[label] = field.value;
                        }
                    } else {
                        if (item_id) {
                            if (!exclusion.data.transactions[item_id]) {
                                exclusion.data.transactions[item_id] = {};
                            }
                            exclusion.data.transactions[item_id][label] = 'No';
                        } else {
                            exclusion.data.transactions[label] = 'No';
                        }
                    }
                } else {
                    if (item_id) {
                        if (!exclusion.data.transactions[item_id]) {
                            exclusion.data.transactions[item_id] = {};
                        }
                        exclusion.data.transactions[item_id][label] = field.value;
                    } else {
                        exclusion.data.transactions[label] = field.value;
                    }
                }
            });
        }

        const error = document.querySelector('label[for="file-upload"] .error')?.innerText;
        const file = document.getElementById('file-upload');

        toggleFieldWarning(file, false);

        if (file.classList.contains('error') && error != 'No File Selected') {
            exclusion.valid = false;
        } else if (file.value != '') {
            toggleFieldWarning(file, true, 'Did you forget to add this file?');
            exclusion.valid = false;
        } else {
            toggleFieldError(file, false);
        }

        if (!exclusion.uploads || exclusion.uploads.length == 0) {
            modal(i18n.getTranslation('error'), 'Please upload supporting documentation.');
            exclusion.valid = false;
        }

        if (!exclusion.valid) {
            const e = new ErrorHandler();
            e.display_modal(exclusion);
            return;
        }

        exclusion.sent = true;
        document.getElementById('submit-claim').disabled = true;

        loadingGif(loading, "show");

        exclusion.api.exclusionPost({}, exclusion.data).then(async (result) => {

            if (result.data.error) {
                loadingGif(loading, "hide", 250);
                exclusion.success = false;
                this.errorHandler.log_exception(new Error('Error submitting exclusion', { cause: result }));
                this.errorHandler.display_modal(result);
                exclusion.inputs.forEach((field) => {
                    field.disabled = true;
                });
                return;
            }

            exclusion.data.claim_id = result.data.claim_id;
            exclusion.data.zero_zero_id = result.data.zero_zero_id;
            exclusion.data.confirmation_code = result.data.confirmation_code;

            if (exclusion.uploads && exclusion.uploads.length > 0) {
                for (var f = 0; f < exclusion.uploads.length; f++) {
                    await this.uploadFile(exclusion, exclusion.uploads[f], f, 0);
                }
            }

            exclusion.success = true;

        }).catch((error) => {
            console.log(error);
            exclusion.success = false;
            this.errorHandler.log_exception(new Error('Error submitting exclusion', { cause: error }));
            this.errorHandler.display_modal(error);
        }).finally(() => {
            loadingGif(loading, "hide", 250);

            if (exclusion.success) {
                exclusion.data.confirmation = this.exclusion_data_confirmation(exclusion.data);
                document.querySelector('div.display-form').classList.add('d-none');
                outputDiv.appendChild(this.exclusion_confirmation(exclusion.data));
                outputDiv.scrollIntoView({ behavior: "smooth" });
            } else {
                this.errorHandler.display_modal(exclusion);
            }

            exclusion.inputs.forEach((field) => {
                field.disabled = true;
            });

            [].forEach.call(document.querySelectorAll('input[type="radio"]'), function (radio) {
                radio.setAttribute('disabled', 'disabled');
            });

            document.getElementById('submit-claim').setAttribute('disabled', 'disabled');
            console.log(`[Exclusion Processed]`);
            console.log(exclusion);
        });
    }

    async uploadFile(exclusion, upload, index, retryCount) {
        retryCount++;

        exclusion.data.files = [];

        let params = {
            domain: exclusion.data.domain,
            zero_zero_id: exclusion.data.zero_zero_id,
            filename: upload.file.name,
            type: upload.file.type,
            extension: upload.file.name.split('.').pop(),
            description: upload.description,
            timestamp: Date.now(),
            index: index,
        };

        // Add Your Key Here!!!
        if (axios.defaults.headers.common["x-api-key"] == undefined) {
            axios.defaults.headers.common = {
                "Content-Type": "application/json",
                "x-api-key": appConfigKey,
            };
        }
        let res = await axios.get(
            this.environment() + '/s3Uploader',
            { params }
        );

        var tags = {
            domain: exclusion.data.domain,
            name: safeTag(upload.file.name),
            description: safeTag(upload.description),
        };

        // Build tag query string
        let tag_query = Object.keys(tags).map(function (k) { return encodeURIComponent(k) + '=' + encodeURIComponent(tags[k]); }).join('&');
        // S3 signed URL headers
        var options = { headers: { 'Content-Type': upload.file.type, 'x-amz-tagging': tag_query } };
        // Put file to S3
        let s3post;
        let errorMessage = "";
        try {
            s3post = await axios.put(res.data.uploadURL, upload.file, options);
        } catch (error) {
            errorMessage = error;
        }

        if (s3post.status == 200) {
            //if succeeded, write to claim_files
            let file = {
                filename: res.data.fileName,
                description: upload.description,
                filetype: upload.file.type,
                error: undefined,
                name: upload.file.name,
                failed: false
            };

            exclusion.data.files.push(file);
            //post claim files:
            var fileData = {
                domain: exclusion.data.domain,
                claim_id: exclusion.data.claim_id,
                zero_zero_id: exclusion.data.zero_zero_id,
                files: [file],
            };
            res = await axios.post(
                this.environment() + '/claims/files',
                fileData
            );

        } else {
            //if failed recursivelly call 5x, then abort
            //retry 3x
            if (retryCount <= 3) {
                wait(1000);
                this.uploadFile(upload, index, retryCount);
            } else {
                if (errorMessage == "") errorMessage = s3post;
                let fileData = {
                    filename: filename,
                    description: upload.description,
                    filetype: upload.file.type,
                    name: upload.file.name,
                    error: JSON.stringify(errorMessage),
                    failed: true
                };
                exclusion.data.files.push(fileData);
            }
        }
    }

    static createInstance({
        appClaimForm,
        userLanguage,
        i18n,
        enableClaimFields = false,
        enableClaimTransactions = false,
        errorHandler = new ErrorHandler(),
    }) {
        return new customOptOut(
            appClaimForm,
            userLanguage,
            i18n,
            enableClaimFields,
            enableClaimTransactions,
            errorHandler
        );
    }
}
